import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
/**
 * What the API can hand a native player for one item's video.
 *
 * `kind: "hls"` is a signed, short-lived stream link — the native player opens
 * it directly, poster first, with picture-in-picture, AirPlay and background
 * audio for free. `kind: "embed"` is the honest answer when no link can be
 * issued (the catalogue spans two Vimeo accounts and the API holds a token for
 * one; or Vimeo is unreachable): the client keeps its embed player. Never an
 * error, so a screen branches on `kind`, and treats a failed query the same
 * way it treats `embed`.
 */
export const VideoStream = z.object({
    kind: z.enum(['hls', 'embed']),
    hls: z.string().nullable(),
    /** ISO 8601; when the stream link stops working. */
    expiresAt: z.string().nullable(),
    poster: z.string().nullable(),
    width: z.number().nullable(),
    height: z.number().nullable(),
    durationSeconds: z.number().nullable(),
});
/** Re-ask this long before the link's own expiry, so a play never lands on a dead one. */
const EXPIRY_MARGIN_MS = 5 * 60 * 1000;
const EMBED_STALE_MS = 60 * 60 * 1000;
/**
 * The stream for an item — for a course, for one of its modules.
 *
 * Fresh until a little before the link expires, then re-fetched on the next
 * read: a member who leaves a course open overnight gets a new link when they
 * come back, not a player that fails on the old one.
 */
export function useContentStream(http, contentId, moduleId) {
    return useQuery({
        queryKey: ['content', 'stream', contentId ?? '', moduleId ?? ''],
        enabled: Boolean(contentId),
        queryFn: () => http.get(`/content/${contentId}/stream`, {
            query: moduleId ? { module: moduleId } : {},
            schema: VideoStream,
        }),
        staleTime: (query) => {
            const expiresAt = query.state.data?.expiresAt;
            if (!expiresAt)
                return EMBED_STALE_MS;
            return Math.max(0, Date.parse(expiresAt) - EXPIRY_MARGIN_MS - Date.now());
        },
        retry: 1,
    });
}
//# sourceMappingURL=stream.js.map